import { supabase } from '../lib/supabase';

export const adminService = {
  /**
   * Get all users (admin only)
   */
  async getAllUsers() {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.order('created_at', { ascending: false });

      if (error) throw error;

      // Convert snake_case to camelCase
      const users = data?.map(user => ({
        id: user?.id,
        name: user?.full_name,
        email: user?.email,
        avatar: user?.avatar_url || '/assets/images/no_image.png',
        avatarAlt: `${user?.full_name} profile picture`,
        role: user?.role,
        status: 'active', // Default status
        sessionsCompleted: 0, // Will be calculated from sessions table
        lastActive: user?.updated_at,
        verificationStatus: 'verified',
        createdAt: user?.created_at,
        updatedAt: user?.updated_at
      })) || [];

      return { data: users, error: null };
    } catch (error) {
      console.error('Get all users error:', error);
      return { data: [], error };
    }
  },

  /**
   * Create new user (admin only)
   * NOTE: This requires Supabase Admin API (service_role) which cannot be used from client.
   * The proper implementation requires:
   * 1. Backend Edge Function with service_role access
   * 2. Call admin.auth.admin.createUser() to create auth.users record
   * 3. The handle_new_user trigger will automatically create user_profiles entry
   * 
   * Current limitation: Client-side admin cannot create users directly.
   * TODO: Implement Edge Function for admin user creation
   */
  async createUser({ fullName, email, role }) {
    try {
      // IMPORTANT: Cannot create auth.users from client (requires service_role)
      // This will fail because user_profiles.id references auth.users(id)
      // and we cannot insert into user_profiles without an auth.users record
      
      return { 
        data: null, 
        error: { 
          message: 'User creation requires backend implementation. Please use Supabase Admin API via Edge Function to create auth.users first.',
          code: 'ADMIN_API_REQUIRED',
          hint: 'Implement an Edge Function that calls supabase.auth.admin.createUser() with service_role access'
        } 
      };
    } catch (error) {
      console.error('Create user error:', error);
      return { data: null, error };
    }
  },

  /**
   * Get all helpers with stats (admin only)
   */
  async getAllHelpers() {
    try {
      const { data, error } = await supabase?.from('helpers')?.select('*')?.order('created_at', { ascending: false });

      if (error) throw error;

      // Convert snake_case to camelCase
      const helpers = data?.map(helper => ({
        id: helper?.id,
        name: helper?.name,
        avatar: helper?.profile_image,
        avatarAlt: helper?.profile_image_alt,
        type: helper?.type_label,
        specialties: helper?.expertise,
        verificationStatus: helper?.verification_status,
        credentialExpiry: helper?.credential_expiry,
        sessionsThisMonth: helper?.sessions_this_month,
        avgRating: helper?.avg_rating,
        workloadStatus: helper?.workload_status,
        lastActive: helper?.last_active
      })) || [];

      return { data: helpers, error: null };
    } catch (error) {
      console.error('Get all helpers error:', error);
      return { data: [], error };
    }
  },

  /**
   * Create new helper profile (admin only)
   */
  async createHelper(helperData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Convert camelCase to snake_case for database
      const dbData = {
        user_id: helperData?.userId || user?.id,
        name: helperData?.name,
        type: helperData?.type || 'licensed',
        type_label: helperData?.typeLabel || 'Licensed Professional',
        profile_image: helperData?.profileImage || '/assets/images/no_image.png',
        profile_image_alt: helperData?.profileImageAlt || `${helperData?.name} profile picture`,
        bio: helperData?.bio || '',
        expertise: helperData?.expertise || [],
        languages: helperData?.languages || ['English'],
        location: helperData?.location || '',
        availability_status: helperData?.availabilityStatus || 'unavailable',
        verification_status: helperData?.verificationStatus || 'pending',
        booking_charge_enabled: helperData?.bookingChargeEnabled || false,
        booking_charge_amount: helperData?.bookingChargeAmount ? parseFloat(helperData?.bookingChargeAmount) : null,
        booking_charge_currency: helperData?.bookingChargeCurrency || 'USD'
      };

      const { data, error } = await supabase?.from('helpers')?.insert([dbData])?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Create helper error:', error);
      return { data: null, error };
    }
  },

  /**
   * Update helper profile (admin only)
   */
  async updateHelper(helperId, updates) {
    try {
      // Convert camelCase to snake_case for database
      const dbUpdates = {};
      if (updates?.name !== undefined) dbUpdates.name = updates?.name;
      if (updates?.type !== undefined) dbUpdates.type = updates?.type;
      if (updates?.typeLabel !== undefined) dbUpdates.type_label = updates?.typeLabel;
      if (updates?.profileImage !== undefined) dbUpdates.profile_image = updates?.profileImage;
      if (updates?.profileImageAlt !== undefined) dbUpdates.profile_image_alt = updates?.profileImageAlt;
      if (updates?.bio !== undefined) dbUpdates.bio = updates?.bio;
      if (updates?.expertise !== undefined) dbUpdates.expertise = updates?.expertise;
      if (updates?.languages !== undefined) dbUpdates.languages = updates?.languages;
      if (updates?.location !== undefined) dbUpdates.location = updates?.location;
      if (updates?.availabilityStatus !== undefined) dbUpdates.availability_status = updates?.availabilityStatus;
      if (updates?.verificationStatus !== undefined) dbUpdates.verification_status = updates?.verificationStatus;
      if (updates?.bookingChargeEnabled !== undefined) dbUpdates.booking_charge_enabled = updates?.bookingChargeEnabled;
      if (updates?.bookingChargeAmount !== undefined) dbUpdates.booking_charge_amount = updates?.bookingChargeAmount ? parseFloat(updates?.bookingChargeAmount) : null;
      if (updates?.bookingChargeCurrency !== undefined) dbUpdates.booking_charge_currency = updates?.bookingChargeCurrency;

      const { data, error } = await supabase?.from('helpers')?.update(dbUpdates)?.eq('id', helperId)?.select();

      if (error) throw error;
      
      // Check if any rows were updated
      if (!data || data?.length === 0) {
        return { data: null, error: { message: `Helper with ID ${helperId} not found` } };
      }
      
      return { data: data?.[0], error: null };
    } catch (error) {
      console.error('Update helper error:', error);
      return { data: null, error };
    }
  },

  /**
   * Get single helper by ID (admin only)
   */
  async getHelperById(helperId) {
    try {
      const { data, error } = await supabase?.from('helpers')?.select('*')?.eq('id', helperId)?.single();

      if (error) throw error;

      // Convert snake_case to camelCase
      const helper = {
        id: data?.id,
        userId: data?.user_id,
        name: data?.name,
        type: data?.type,
        typeLabel: data?.type_label,
        profileImage: data?.profile_image,
        profileImageAlt: data?.profile_image_alt,
        bio: data?.bio,
        expertise: data?.expertise,
        languages: data?.languages,
        location: data?.location,
        availabilityStatus: data?.availability_status,
        verificationStatus: data?.verification_status,
        rating: data?.rating,
        reviewCount: data?.review_count,
        sessionsCompleted: data?.sessions_completed,
        workloadStatus: data?.workload_status,
        sessionsThisMonth: data?.sessions_this_month,
        avgRating: data?.avg_rating,
        credentialExpiry: data?.credential_expiry,
        lastActive: data?.last_active,
        bookingChargeEnabled: data?.booking_charge_enabled,
        bookingChargeAmount: data?.booking_charge_amount,
        bookingChargeCurrency: data?.booking_charge_currency
      };

      return { data: helper, error: null };
    } catch (error) {
      console.error('Get helper by ID error:', error);
      return { data: null, error };
    }
  },

  /**
   * Update user role (admin only)
   */
  async updateUserRole(userId, role) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({ role })?.eq('id', userId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Update user role error:', error);
      return { data: null, error };
    }
  },

  /**
   * Update helper verification status (admin only)
   */
  async updateHelperVerification(helperId, verificationStatus) {
    try {
      const { data, error } = await supabase?.from('helpers')?.update({ verification_status: verificationStatus })?.eq('id', helperId)?.select();

      if (error) throw error;
      
      // Check if any rows were updated
      if (!data || data?.length === 0) {
        return { data: null, error: { message: `Helper with ID ${helperId} not found` } };
      }
      
      return { data: data?.[0], error: null };
    } catch (error) {
      console.error('Update helper verification error:', error);
      return { data: null, error: { message: error?.message || 'Failed to update helper verification' } };
    }
  },

  /**
   * Update user (admin only)
   */
  async updateUser(userId, updates) {
    try {
      // Convert camelCase to snake_case for database
      const dbUpdates = {
        ...(updates?.fullName && { full_name: updates?.fullName }),
        ...(updates?.email && { email: updates?.email }),
        ...(updates?.role && { role: updates?.role })
      };

      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update(dbUpdates)
        ?.eq('id', userId)
        ?.select();

      if (error) throw error;
      
      // Check if any rows were updated
      if (!data || data?.length === 0) {
        return { data: null, error: { message: `User with ID ${userId} not found` } };
      }
      
      return { data: data?.[0], error: null };
    } catch (error) {
      console.error('Update user error:', error);
      return { data: null, error };
    }
  },

  /**
   * Delete user (admin only)
   * NOTE: This only deletes the user_profiles record.
   * The auth.users record should be deleted via Supabase Admin API.
   */
  async deleteUser(userId) {
    try {
      const { error } = await supabase
        ?.from('user_profiles')
        ?.delete()
        ?.eq('id', userId);

      if (error) throw error;
      return { data: true, error: null };
    } catch (error) {
      console.error('Delete user error:', error);
      return { data: null, error };
    }
  },

  /**
   * Suspend user (admin only)
   * This sets a suspended flag or status in user_profiles
   */
  async suspendUser(userId) {
    try {
      // Add a suspended field or update status
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update({ status: 'suspended' })
        ?.eq('id', userId)
        ?.select();

      if (error) throw error;
      
      // Check if any rows were updated
      if (!data || data?.length === 0) {
        return { data: null, error: { message: `User with ID ${userId} not found` } };
      }
      
      return { data: data?.[0], error: null };
    } catch (error) {
      console.error('Suspend user error:', error);
      return { data: null, error };
    }
  },

  /**
   * Get organization settings (admin only)
   */
  async getOrganization() {
    try {
      const { data, error } = await supabase?.from('organizations')?.select('*')?.limit(1)?.single();

      if (error && error?.code !== 'PGRST116') throw error; // Ignore "not found" error

      // Convert snake_case to camelCase
      const organization = data ? {
        id: data?.id,
        name: data?.name,
        domain: data?.domain,
        settings: data?.settings,
        branding: data?.branding,
        supportEmail: data?.support_email,
        timezone: data?.timezone,
        ssoProvider: data?.sso_provider,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      } : null;

      return { data: organization, error: null };
    } catch (error) {
      console.error('Get organization error:', error);
      return { data: null, error };
    }
  },

  /**
   * Create organization (admin only)
   */
  async createOrganization(orgData) {
    try {
      // Convert camelCase to snake_case for database
      const dbData = {
        name: orgData?.name,
        domain: orgData?.domain,
        settings: orgData?.settings,
        branding: orgData?.branding,
        support_email: orgData?.supportEmail,
        timezone: orgData?.timezone,
        sso_provider: orgData?.ssoProvider
      };

      const { data, error } = await supabase?.from('organizations')?.insert([dbData])?.select()?.single();

      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      console.error('Create organization error:', error);
      return { data: null, error };
    }
  },

  /**
   * Update organization settings (admin only)
   */
  async updateOrganization(orgId, updates) {
    try {
      // Convert camelCase to snake_case for database
      const dbUpdates = {
        ...(updates?.name && { name: updates?.name }),
        ...(updates?.domain && { domain: updates?.domain }),
        ...(updates?.settings && { settings: updates?.settings }),
        ...(updates?.branding && { branding: updates?.branding }),
        ...(updates?.supportEmail && { support_email: updates?.supportEmail }),
        ...(updates?.timezone && { timezone: updates?.timezone }),
        ...(updates?.ssoProvider && { sso_provider: updates?.ssoProvider })
      };

      const { data, error } = await supabase?.from('organizations')?.update(dbUpdates)?.eq('id', orgId)?.select();

      if (error) throw error;
      
      // Check if organization was found and updated
      if (!data || data?.length === 0) {
        return { 
          data: null, 
          error: { message: `Organization with ID ${orgId} not found` } 
        };
      }
      
      return { data: data?.[0], error: null };
    } catch (error) {
      console.error('Update organization error:', error);
      return { data: null, error };
    }
  },

  /**
   * Get platform statistics (admin only)
   */
  async getPlatformStats() {
    try {
      // Get total users
      const { count: totalUsers } = await supabase?.from('user_profiles')?.select('*', { count: 'exact', head: true });

      // Get total helpers
      const { count: totalHelpers } = await supabase?.from('helpers')?.select('*', { count: 'exact', head: true });

      // Get total sessions
      const { count: totalSessions } = await supabase?.from('sessions')?.select('*', { count: 'exact', head: true });

      // Get active sessions
      const { count: activeSessions } = await supabase?.from('sessions')?.select('*', { count: 'exact', head: true })?.eq('status', 'active');

      return {
        data: {
          totalUsers: totalUsers || 0,
          totalHelpers: totalHelpers || 0,
          totalSessions: totalSessions || 0,
          activeSessions: activeSessions || 0
        },
        error: null
      };
    } catch (error) {
      console.error('Get platform stats error:', error);
      return { data: null, error };
    }
  },

  /**
   * Get usage analytics for admin dashboard
   * @param {string} timeRange - Time range for analytics
   * @returns {Promise<Object>} Usage analytics data
   */
  async getUsageAnalytics(timeRange = '30days') {
    try {
      const daysMap = {
        '7days': 7,
        '30days': 30,
        '90days': 90,
        '1year': 365
      };
      
      const days = daysMap?.[timeRange] || 30;
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - days);

      // Get appointments for booking trends
      const { data: appointments, error: apptError } = await supabase
        ?.from('appointments')
        ?.select('appointment_date, status, created_at')
        ?.gte('created_at', startDate?.toISOString())
        ?.order('appointment_date', { ascending: true });

      if (apptError) throw apptError;

      // Get sessions for completion rate
      const { data: sessions, error: sessError } = await supabase
        ?.from('sessions')
        ?.select('status, start_time')
        ?.gte('start_time', startDate?.toISOString());

      if (sessError) throw sessError;

      // Get helpers by type
      const { data: helpers, error: helpError } = await supabase
        ?.from('helpers')
        ?.select('type, type_label');

      if (helpError) throw helpError;

      // Get seeker intakes for issue tracking
      const { data: intakes, error: intakeError } = await supabase
        ?.from('seeker_intakes')
        ?.select('current_issues')
        ?.gte('created_at', startDate?.toISOString());

      if (intakeError) throw intakeError;

      // Calculate booking trends (group by date)
      const bookingTrends = this._groupAppointmentsByDate(appointments);

      // Calculate helper type distribution
      const helperTypeDistribution = this._calculateHelperDistribution(helpers);

      // Calculate top issues
      const topIssues = this._calculateTopIssues(intakes);

      // Calculate KPIs
      const completedSessions = sessions?.filter(s => s?.status === 'completed')?.length || 0;
      const totalSessions = sessions?.length || 0;
      const completionRate = totalSessions > 0 ? ((completedSessions / totalSessions) * 100)?.toFixed(1) : '0.0';

      const scheduledAppointments = appointments?.filter(a => a?.status === 'scheduled' || a?.status === 'upcoming')?.length || 0;
      const totalAppointments = appointments?.length || 0;
      const acceptanceRate = totalAppointments > 0 ? ((scheduledAppointments / totalAppointments) * 100)?.toFixed(1) : '0.0';

      // Get active users count
      const { count: activeUsers } = await supabase
        ?.from('user_profiles')
        ?.select('*', { count: 'exact', head: true });

      return {
        data: {
          kpis: {
            avgTimeToBooking: '1.8 min', // This would need session timing data
            matchAcceptanceRate: `${acceptanceRate}%`,
            sessionCompletion: `${completionRate}%`,
            activeUsers: activeUsers || 0
          },
          bookingTrends,
          helperTypeDistribution,
          topIssues
        },
        error: null
      };
    } catch (error) {
      console.error('Get usage analytics error:', error);
      return { data: null, error };
    }
  },

  /**
   * Group appointments by date for trend chart
   * @private
   */
  _groupAppointmentsByDate(appointments) {
    const grouped = {};
    
    appointments?.forEach(appt => {
      const date = new Date(appt?.appointment_date || appt?.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      if (!grouped?.[date]) {
        grouped[date] = { date, bookings: 0, completions: 0 };
      }
      
      grouped[date].bookings++;
      if (appt?.status === 'completed') {
        grouped[date].completions++;
      }
    });

    return Object.values(grouped)?.slice(-8); // Last 8 data points
  },

  /**
   * Calculate helper type distribution
   * @private
   */
  _calculateHelperDistribution(helpers) {
    const distribution = {};
    const colors = {
      'licensed': '#4A90A4',
      'lived-experience': '#7B68A6',
      'peer-support': '#E8A87C'
    };

    helpers?.forEach(helper => {
      const label = helper?.type_label || 'Other';
      if (!distribution?.[label]) {
        distribution[label] = { name: label, value: 0, color: colors?.[helper?.type] || '#999' };
      }
      distribution[label].value++;
    });

    return Object.values(distribution);
  },

  /**
   * Calculate top issues from intakes
   * @private
   */
  _calculateTopIssues(intakes) {
    const issueCounts = {};

    intakes?.forEach(intake => {
      const issues = intake?.current_issues || [];
      issues?.forEach(issue => {
        if (!issueCounts?.[issue]) {
          issueCounts[issue] = 0;
        }
        issueCounts[issue]++;
      });
    });

    // Convert to array and sort by count
    const issuesArray = Object.entries(issueCounts)?.map(([issue, sessions]) => ({ issue, sessions }))?.sort((a, b) => b?.sessions - a?.sessions)?.slice(0, 5); // Top 5

    return issuesArray;
  }
};